import { createPortal } from 'react-dom';

import { useTranslation } from '@mint-lib/i18n';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Card,
} from '@mint-lib/tw-components';

import { usePerformanceRouteApi } from '../../hooks/useImportHubRouteApi.js';
import { useGlossaryList } from '../../queries/index.js';
import { EmptyState } from '../atoms/EmptyState.jsx';
import { AddGlossaryItemButton } from '../molecules/AddGlossaryItemButton.jsx';
import { GlossaryTable } from '../molecules/GlossaryTable.jsx';
import { RouterSearchBar } from '../molecules/RouterSearchBar.jsx';
import { GlossaryTableSkeleton } from './GlossaryTableSkeleton.jsx';

export default function GlossaryTab() {
  const { t } = useTranslation('@myn/import-hub');

  const searchParams = usePerformanceRouteApi().useSearch();

  const {
    isPending,
    platformsGlossaryList,
    channelsGlossaryList,
    platformsCatalog,
    channelsCatalog,
    isAllPlatformGlossariesAdded,
    isAllChannelGlossariesAdded,
  } = useGlossaryList();

  const isEmptyPlatformGlossariesArray =
    !isPending && !platformsGlossaryList.totalItems;

  const isEmptyChannelsGlossariesArray =
    !isPending && !channelsGlossaryList.totalItems;

  const importHubHeaderContainer = globalThis.document.getElementById(
    'hub-header-container',
  );

  const infoAlert = importHubHeaderContainer
    ? createPortal(
        <Alert severity="info" variant="inline" className="mb-4">
          <AlertTitle className="flex items-center">{t('Glossary')}</AlertTitle>
          <AlertDescription className="flex items-center">
            {t(
              `Remember to set up a naming convention for channels and platforms in the Glossary section.`,
            )}
          </AlertDescription>
        </Alert>,
        importHubHeaderContainer,
      )
    : null;

  if (
    isEmptyPlatformGlossariesArray &&
    isEmptyChannelsGlossariesArray &&
    !searchParams.search
  ) {
    return (
      <>
        {infoAlert}
        <EmptyState
          icon="IllustrationEmptyState"
          text={t('There are no items in this glossary')}
          className="border-none bg-transparent shadow-none"
        >
          <AddGlossaryItemButton
            isPending={isPending}
            platformsGlossaryData={platformsGlossaryList}
            channelsGlossaryData={channelsGlossaryList}
            isPlatformAddingDisabled={isAllPlatformGlossariesAdded}
            isChannelsAddingDisabled={isAllChannelGlossariesAdded}
            className="mt-4"
          />
        </EmptyState>
      </>
    );
  }

  return (
    <>
      {infoAlert}
      <Card
        variant="default"
        className="mb-2 w-full"
        contentClassName="flex items-center w-full gap-4"
      >
        <AddGlossaryItemButton
          isPending={isPending}
          platformsGlossaryData={platformsGlossaryList}
          channelsGlossaryData={channelsGlossaryList}
          isChannelsAddingDisabled={isAllChannelGlossariesAdded}
          isPlatformAddingDisabled={isAllPlatformGlossariesAdded}
        />
        <RouterSearchBar
          placeholder={t('Search for name')}
          className="flex-1"
        />
      </Card>

      {isPending ? (
        <GlossaryTableSkeleton />
      ) : (
        <GlossaryTable
          searchParams={searchParams}
          platformsGlossaryList={platformsGlossaryList}
          channelsGlossaryList={channelsGlossaryList}
          channelsByName={channelsCatalog.channelsByName}
          platformsByName={platformsCatalog.platformsByName}
        />
      )}
    </>
  );
}
